import AHDate from "_shared/dates/date";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";

export default class NetworkInvitationQuery {
  public readonly filterParameters: NetworkInvitationFilterParameters = new NetworkInvitationFilterParameters({});
  public readonly orderParameters: NetworkInvitationOrderParameters = new NetworkInvitationOrderParameters();
  public readonly paginationParameters: PaginationParameters = PaginationParameters.create({
    pageIndex: 0,
    pageSize: 25,
  });
  public readonly query?: string;

  public get searchParams(): URLSearchParams {
    const searchParams = new URLSearchParams([
      ...this.paginationParameters.searchParams,
      ...this.orderParameters.searchParams,
      ...this.filterParameters.searchParams,
    ]);
    if (this.query) searchParams.append("query", this.query);
    return searchParams;
  }

  public constructor(params: Partial<NetworkInvitationQuery> = {}) {
    Object.assign(this, params);
  }

  public isEqualTo = (other: NetworkInvitationQuery): boolean =>
    this.filterParameters.isEqualTo(other.filterParameters) &&
    this.orderParameters.isEqualTo(other.orderParameters) &&
    this.paginationParameters.isEqualTo(other.paginationParameters);
}

export class NetworkInvitationFilterParameters {
  public readonly name?: string;
  public readonly message?: string;
  public readonly status?: string;
  public readonly includeRejected: boolean = false;
  public readonly minSentDate?: AHDate;
  public readonly maxSentDate?: AHDate;
  /** Received keeps the invitations sent to the caller, Sent the ones they sent; unset keeps both. */
  public readonly direction?: NetworkInvitationDirection;

  public get searchParams(): URLSearchParams {
    const searchParams = new URLSearchParams();
    if (this.name) searchParams.append("name", this.name);
    if (this.message) searchParams.append("message", this.message);
    if (this.status) searchParams.append("status", this.status);
    if (this.includeRejected) searchParams.append("includeRejected", this.includeRejected.toString());
    if (this.minSentDate) searchParams.append("minSentDate", this.minSentDate.toISOString());
    if (this.maxSentDate) searchParams.append("maxSentDate", this.maxSentDate.toISOString());
    if (this.direction) searchParams.append("direction", this.direction);
    return searchParams;
  }

  public constructor(values: Partial<NetworkInvitationFilterParameters> | undefined = undefined) {
    if (values) Object.assign(this, values);
  }

  public isEqualTo = (other: NetworkInvitationFilterParameters): boolean =>
    this.name === other.name &&
    this.message === other.message &&
    this.status === other.status &&
    this.includeRejected === other.includeRejected &&
    this.direction === other.direction &&
    (this.minSentDate?.isEqualTo(other.minSentDate) || (!this.minSentDate && !other.minSentDate)) &&
    (this.maxSentDate?.isEqualTo(other.maxSentDate) || (!this.maxSentDate && !other.maxSentDate));
}

export class NetworkInvitationOrderParameters {
  public get searchParams(): URLSearchParams {
    const params = new URLSearchParams();
    if (this.orderBy) params.append("orderBy", this.orderBy);
    if (this.orderDir) params.append("orderDir", this.orderDir);
    return params;
  }

  public constructor(
    public readonly orderBy: NetworkInvitationOrderField | undefined = undefined,
    public readonly orderDir: OrderDirection | undefined = undefined
  ) {}

  public isEqualTo = (other: NetworkInvitationOrderParameters): boolean =>
    this.orderBy === other.orderBy && this.orderDir === other.orderDir;
}

export enum NetworkInvitationOrderField {
  Name = "Name",
  Message = "Message",
  Status = "Status",
  /** The server's name for the sent date (`NetworkInvitationOrderField.Sent`); "SentDate" binds to nothing. */
  Sent = "Sent",
}

/** The server's `NetworkInvitationDirection`, seen from the caller. */
export enum NetworkInvitationDirection {
  Received = "Received",
  Sent = "Sent",
}
